import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Checkbox from '@repo/ui/checkbox'
import DataTable, { Column } from '@repo/ui/data-table'
import { CheckMarkIcon, ExclamationMarkOutlinedIcon, StarFilledIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { getCenterGroups } from '../../../api/centers/center-groups/get-center-groups'
import { getCenterEnrollmentsAgeCompliance } from '../../../api/centers/get-center-enrollments-age-compliance'
import { getCenters } from '../../../api/centers/get-centers'
import { getEnrollments } from '../../../api/enrollments/get-enrollments'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { schoolLevelEnumMapping } from '../../../lib/types/enums/common/school-level.enum'
import { applicationStatusEnumMapping } from '../../../lib/types/enums/enrollment/application-status.enum'
import { friendPlaceEnumMapping } from '../../../lib/types/enums/friend/friend-place.enum'
import {
  enrollmentAffectedEnrollmentFormFields,
  EnrollmentAffectedEnrollmentFormType
} from '../../../lib/types/fields/enrollments/enrollment'
import { CenterLight } from '../../../lib/types/models/center/center'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { CenterAgeComplianceProperties } from '../../../lib/types/models/center/properties.center-age-compliance'
import { CenterGroup } from '../../../lib/types/models/center-group/center-group'
import {
  AffectEnrollmentFailure,
  AffectEnrollmentInput
} from '../../../lib/types/models/enrollment/affect-enrollment'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import ActionButtons from '../../elements/action-buttons'
import { CenterCapsuleInformation } from './shared/center-capsule-information'

interface Props {
  className?: string
  enrollmentIds: number[]
  // the students the server refused to place on the last save, with the reason
  failures?: AffectEnrollmentFailure[]
  isLoading?: boolean
  onCancel: () => void
  handleSubmit: (values: AffectEnrollmentInput[]) => void
}

type GroupSelectInputProps = {
  centerId?: number
  selectedGroupId?: number
  isDisabled: boolean
  onChange: (groupId?: number) => void
}

const GroupSelectInput = ({
  centerId,
  selectedGroupId,
  isDisabled,
  onChange
}: GroupSelectInputProps) => {
  const { data } = useQuery({
    queryKey: ['center-groups', centerId],
    queryFn: () => {
      if (!centerId) return Promise.resolve({ data: [] } as { data: CenterGroup[] })
      return getCenterGroups(centerId)
    }
  })

  const groups = data?.data ?? []

  return (
    <SelectInput
      label="Groupe"
      dropDownPosition="above"
      disabled={isDisabled || groups.length === 0}
      searchable
      options={groups.map((group) => ({ value: group.id, label: group.name }))}
      selectedOption={getSelectedOption(
        selectedGroupId,
        groups.find((group) => group.id === selectedGroupId)?.name
      )}
      onChange={(groupId) => {
        onChange(groupId)
      }}
    />
  )
}

export default function AffectEnrollmentsForm({
  className,
  enrollmentIds,
  failures = [],
  isLoading,
  onCancel,
  handleSubmit
}: Props) {
  const { t } = useTranslate()
  const [input, setInput] = useState<AffectEnrollmentInput[]>([])

  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: () => getCenters()
  })

  const { data } = useQuery({
    queryKey: ['enrollments', enrollmentIds, enrollmentAffectedEnrollmentFormFields],
    queryFn: () =>
      getEnrollments({ filter: { ids: enrollmentIds } }, enrollmentAffectedEnrollmentFormFields)
  })

  const { modalState, toggleModal, closeModel } = useModalManager<'age-mismatch'>(['age-mismatch'])

  const centerAssignments = useMemo(
    () => CenterAgeComplianceProperties.groupEnrollmentIdsByCenter(input),
    [input]
  )
  const ageComplianceQueries = useQueries({
    queries: centerAssignments.map(({ centerId, enrollmentIds: ids }) => ({
      queryKey: ['center-enrollments-age-compliance', centerId, ids],
      queryFn: () => getCenterEnrollmentsAgeCompliance(centerId, ids)
    }))
  })
  const complianceByEnrollmentId = CenterAgeComplianceProperties.mapByEnrollmentId(
    ageComplianceQueries.map((query) => query.data)
  )

  const incompatibleAgeRows = CenterAgeComplianceProperties.getIncompatibleRows(
    input,
    data?.data ?? [],
    complianceByEnrollmentId
  )

  const isCenterAvailable = (center: CenterLight) => {
    const centerPlaces = input.filter((i) => i.center?.id === center?.id)
    const reservedPlaces = centerPlaces.reduce((acc, i) => acc + 1 + i.blockedFriendIds.length, 0)
    return CenterProperties.getAvailablePlaces(center) - reservedPlaces >= 0
  }

  const enrollments = useMemo(
    () => data?.data.filter((e) => enrollmentIds.includes(e.id)),
    [data, enrollmentIds]
  )
  // only the confirmed students can be placed, the others are listed as they are
  const affectableEnrollments = useMemo(
    () => enrollments?.filter((enrollment) => EnrollmentProperties.isAffectable(enrollment)),
    [enrollments]
  )

  const initializeInput = () => {
    if (!affectableEnrollments || !centers) return
    setInput((prev) =>
      affectableEnrollments.map((enrollment) => {
        // keep the choices made before a save that refused this student
        const previous = prev.find((i) => i.enrollmentId === enrollment.id)
        if (previous) return previous
        const journeyId = enrollment.mainProposition?.journey.id
        const enrollmentCenters = centers?.data.filter(
          (c) =>
            c.centerToJourneys.some((ctj) => ctj.journeyId === journeyId) &&
            c.tripPlacesRequests.some(
              (t) =>
                t.trip.id === enrollment?.mainProposition?.mainTrip?.id ||
                t.trip.id === enrollment?.mainProposition?.mainTrip?.linkedToMainTripId
            ) &&
            EnrollmentProperties.hasCompatibleCenterAccomodation(enrollment, c) &&
            !c.locked
        )

        return {
          enrollmentId: enrollment.id,
          blockedFriendIds:
            enrollment.declaredFriends?.filter((f) => !!f.blockedPlaceId).map((f) => f.id) ?? [],
          center: enrollmentCenters.length === 1 ? enrollmentCenters[0] : undefined
        }
      })
    )
  }

  useEffect(() => {
    initializeInput()
  }, [affectableEnrollments, centers])

  const columns: Column<EnrollmentAffectedEnrollmentFormType>[] = [
    {
      accessorKey: 'childFirstName',
      header: 'NOM',
      cell: ({ row }) => {
        const { childFirstName, childLastName, mainProposition } = row.original
        const failure = failures.find((f) => f.enrollmentId === row.original.id)
        const dates = EnrollmentProperties.getEnrollmentDates(row.original).value
        const formula = EnrollmentProperties.getEnrollmentFormula(row.original).value
        const mainOption = EnrollmentProperties.getEnrollmentMainOption(row.original).value
        const options = EnrollmentProperties.getEnrollmentOptions(row.original).value
        const hoursPerWeek = EnrollmentProperties.getEnrollmentHoursPerWeek(row.original).value
        return (
          <div className="flex flex-col gap-2 my-2">
            <div className="flex flex-row items-center gap-2">
              <Typography variant="secondary-body">
                <span className="font-semibold">{childLastName?.toUpperCase()}</span>{' '}
                {childFirstName ? StringFormatter.capitalize(childFirstName) : '-'}
              </Typography>
              {row.original.isAA && <StarFilledIcon width="24px" height="24px" />}
            </div>
            {!EnrollmentProperties.isAffectable(row.original) && (
              <Badge
                className="w-fit"
                size="small"
                textVariant="small-medium"
                variant={applicationStatusEnumMapping[row.original.status].variant}
                text={applicationStatusEnumMapping[row.original.status].label}
              />
            )}
            {failure && (
              <Badge
                variant="danger"
                icon={
                  <ExclamationMarkOutlinedIcon
                    width="16px"
                    height="16px"
                    color={tailwindColors.red.DEFAULT}
                  />
                }
                className="w-fit max-w-96"
                textClassName="text-wrap"
                size="small"
                iconPosition="start"
                textVariant="small-medium"
                text={failure.message}
              />
            )}
            <div className="p-2 bg-white-hover border border-grey-light rounded w-fit">
              <Typography variant="label-medium" className="text-grey">
                {dates ? `${dates.startDate} - ${dates.endDate}` : '-'}
              </Typography>
              {formula && (
                <Typography variant="label-medium" className="text-grey">
                  {formula}
                </Typography>
              )}
              {mainOption && (
                <Typography variant="label-medium" className="text-grey">
                  {mainOption}
                </Typography>
              )}
              {options && (
                <Typography variant="label-medium" className="text-grey">
                  {options}
                </Typography>
              )}
              {hoursPerWeek !== undefined && (
                <Typography variant="label-medium" className="text-grey">
                  {t('product.number.of.hours.per.week', { hours: hoursPerWeek })}
                </Typography>
              )}
              {/* a One to One Individuel enrollment never carries a trip */}
              {mainProposition?.mainTrip && (
                <Typography variant="label-medium" className="text-grey">
                  {TripProperties.getTripLabel(mainProposition.mainTrip)}
                </Typography>
              )}
              {mainProposition?.secondaryTrip && (
                <Typography variant="label-medium" className="text-grey">
                  {TripProperties.getTripLabel(mainProposition.secondaryTrip)}
                </Typography>
              )}
            </div>
          </div>
        )
      }
    },
    {
      accessorKey: 'childGender',
      header: 'Sexe',
      cell: ({ row }) =>
        row.original.childGender ? genderOneLetterMapping[row.original.childGender].label : '-'
    },
    {
      accessorKey: 'childBirthDate',
      header: 'Âge',
      invertSorting: true,
      cell: ({ row }) => {
        const compliance = complianceByEnrollmentId.get(row.original.id)
        const age = compliance?.ageAtDeparture ?? row.original.childAgeAtDeparture
        if (age === null || age === undefined) return '-'
        if (!CenterAgeComplianceProperties.isIncompatible(compliance)) return age
        return (
          <div className="w-7 h-7 min-w-7 shrink-0 rounded-full bg-red-light inline-flex items-center justify-center">
            <Typography variant="label-semibold" color={tailwindColors.red.DEFAULT}>
              {age}
            </Typography>
          </div>
        )
      }
    },
    {
      accessorKey: 'childSchoolLevel',
      header: 'Classe',
      cell: ({ row }) =>
        row.original.childSchoolLevel ? schoolLevelEnumMapping[row.original.childSchoolLevel] : '-'
    },
    {
      accessorKey: 'childNumberOfYearsInLanguage',
      header: `Années d'étude`,
      cell: ({ row }) =>
        row.original.childNumberOfYearsInLanguage ? row.original.childNumberOfYearsInLanguage : '-'
    },
    {
      accessorKey: 'declaredFriends',
      header: `Bloquer une place`,
      cell: ({ row }) => (
        <div className="flex flex-col gap-2">
          {row.original.declaredFriends?.map((friend) => (
            <div key={friend.id} className="p-1 rounded-sm border border-grey-light w-fit">
              <Checkbox
                label={`${toFullName(friend.firstName, friend.lastName)} - ${friendPlaceEnumMapping[friend.type].title}`}
                checked={input.some((i) => i.blockedFriendIds.includes(friend.id))}
                onChange={(value) => {
                  setInput(
                    input.map((i) => {
                      if (i.enrollmentId === row.original.id) {
                        if (value)
                          return {
                            ...i,
                            blockedFriendIds: [...i.blockedFriendIds, friend.id]
                          }
                        return {
                          ...i,
                          blockedFriendIds: i.blockedFriendIds.filter((id) => id !== friend.id)
                        }
                      }
                      return i
                    })
                  )
                }}
              />
            </div>
          ))}
        </div>
      )
    },
    {
      accessorKey: 'childNumberOfYearsInLanguage',
      header: 'Région de préférence',
      cell: ({ row }) => row.original.mainProposition?.preferredPlace?.name ?? '-'
    },
    {
      accessorKey: 'center',
      header: 'Centre',
      cell: ({ row }) => {
        const selectedCenter = input.find((i) => i.enrollmentId === row.original.id)?.center
        const enrollment = data?.data.find((e) => e.id === row.original.id)
        if (!enrollment) return null
        if (!EnrollmentProperties.isAffectable(enrollment)) return '-'
        const journeyId = enrollment?.mainProposition?.journey.id
        const enrollmentCenters = centers?.data.filter(
          (c) =>
            c.centerToJourneys.some((ctj) => ctj.journeyId === journeyId) &&
            c.tripPlacesRequests.some(
              (t) =>
                t.trip.id === enrollment?.mainProposition?.mainTrip?.id ||
                t.trip.id === enrollment?.mainProposition?.mainTrip?.linkedToMainTripId
            ) &&
            EnrollmentProperties.hasCompatibleCenterAccomodation(enrollment, c) &&
            !c.locked
        )
        if (!enrollmentCenters) return null

        // If there's exactly one available center, don't show a dropdown — display the center name instead
        if (enrollmentCenters.length === 1) {
          const onlyCenter = enrollmentCenters[0]
          return (
            <div className="flex flex-col gap-2 w-52">
              <Typography variant="label-medium">{onlyCenter.name}</Typography>
              {(!isCenterAvailable(onlyCenter) ||
                (selectedCenter && !isCenterAvailable(selectedCenter))) && (
                <Badge
                  variant="danger"
                  icon={
                    <ExclamationMarkOutlinedIcon
                      width="16px"
                      height="16px"
                      color={tailwindColors.red.DEFAULT}
                    />
                  }
                  textClassName="text-wrap"
                  size="small"
                  iconPosition="start"
                  textVariant="small-medium"
                  text={'Attention, vous allez dépasser la capacité maximale du centre.'}
                />
              )}
            </div>
          )
        }

        return (
          <div className="flex flex-col gap-2 w-52">
            <SelectInput
              label="Centre"
              dropDownPosition="above"
              disabled={enrollmentCenters.length === 0}
              searchable
              options={
                enrollmentCenters.map((center) => ({ value: center.id, label: center.name })) ?? []
              }
              selectedOption={getSelectedOption(
                selectedCenter?.id,
                enrollmentCenters.find((center) => center.id === selectedCenter?.id)?.name
              )}
              onChange={(center) => {
                const result = enrollmentCenters.find((c) => c.id === center)
                if (result) {
                  setInput((prev) =>
                    prev.map((i) => {
                      if (i.enrollmentId === row.original.id)
                        return { ...i, center: result, groupId: undefined }
                      return i
                    })
                  )
                }
              }}
            />
            {selectedCenter && !isCenterAvailable(selectedCenter) && (
              <Badge
                variant="danger"
                icon={
                  <ExclamationMarkOutlinedIcon
                    width="16px"
                    height="16px"
                    color={tailwindColors.red.DEFAULT}
                  />
                }
                textClassName="text-wrap"
                size="small"
                iconPosition="start"
                textVariant="small-medium"
                text={'Attention, vous allez dépasser la capacité maximale du centre.'}
              />
            )}
          </div>
        )
      }
    },

    {
      accessorKey: 'centerGroup',
      header: 'Groupe',
      cell: ({ row }) => {
        const enrollmentInput = input.find((i) => i.enrollmentId === row.original.id)
        if (!enrollmentInput) return '-'
        const selectedCenter = enrollmentInput.center
        const selectedGroupId = enrollmentInput.centerGroupId

        return (
          <GroupSelectInput
            centerId={selectedCenter?.id}
            selectedGroupId={selectedGroupId}
            isDisabled={!selectedCenter}
            onChange={(centerGroupId) => {
              setInput((prev) =>
                prev.map((i) => {
                  if (i.enrollmentId === row.original.id) return { ...i, centerGroupId }
                  return i
                })
              )
            }}
          />
        )
      }
    },

    {
      accessorKey: 'center',
      header: 'Infos centre',
      cell: ({ row }) => {
        const center = input.find((i) => i.enrollmentId === row.original.id)?.center
        const compliance = complianceByEnrollmentId.get(row.original.id)
        const { isAgeIncompatible, isMinAgeViolated, isMaxAgeViolated } =
          CenterAgeComplianceProperties.getViolations(compliance)
        return center ? (
          <div className="my-4 px-3 py-2 bg-white-hover border border-grey-light flex gap-3 rounded">
            <div className="flex flex-col gap-3">
              <div>
                <Typography variant="small-semibold" className="text-grey">
                  # Places max : {center.maxNumberOfPlaces} - # Places restantes :{' '}
                  {CenterProperties.getAvailablePlaces(center)}
                </Typography>
                <div className="h-1" />
                <CenterCapsuleInformation
                  hideTotal
                  attributedPlaces={center.affectedStudentsNumber}
                  blockedPlaces={center.blockedPlacesNumber}
                  total={center.maxNumberOfPlaces}
                />
              </div>
              <div className="flex justify-between gap-3 items-center">
                <Typography variant="small-medium" className="text-grey">
                  Proportion
                </Typography>

                <div className="flex gap-1 items-center">
                  <Badge
                    size="xsmall"
                    variant="fushia"
                    textVariant="small-semibold"
                    text={`Filles : ${Math.round((center.femaleRatio ?? 0) * 100)}%`}
                  />
                  <Badge
                    size="xsmall"
                    variant="blue"
                    textVariant="small-semibold"
                    text={`Garçons : ${Math.round((center.maleRatio ?? 0) * 100)}%`}
                  />
                </div>
              </div>

              <div className="flex gap-1 items-center flex-wrap">
                <Badge
                  size="xsmall"
                  variant={isMinAgeViolated ? 'danger' : 'grey'}
                  textVariant="small-semibold"
                  text={`Âge min : ${valueRender(center.minAge, (age) => `${age} ans`)}`}
                />
                <Badge
                  size="xsmall"
                  textVariant="small-semibold"
                  variant={isMaxAgeViolated ? 'danger' : 'grey'}
                  text={`Âge max : ${valueRender(center.maxAge, (age) => `${age} ans`)}`}
                />
                <Badge
                  size="xsmall"
                  textVariant="small-semibold"
                  variant="grey"
                  text={`Âge moyen : ${valueRender(center.affectedStudentsAgeAverage, (age) => `${age} ans`)}`}
                />
              </div>
              {isAgeIncompatible && (
                <Typography variant="small-semibold" color={tailwindColors.red.DEFAULT}>
                  Âge incompatible avec le centre
                </Typography>
              )}
            </div>

            <div className="w-px h-auto bg-grey-light" />

            <div>
              <Typography variant="small-semibold" className="text-grey">
                Les voyages concernés
              </Typography>
              <div className="h-2" />
              <div className="flex flex-col gap-1.5">
                {center.tripPlacesRequests.map((tripPlace) => (
                  <div
                    key={tripPlace.id}
                    className="border border-grey-light bg-white-active rounded-sm px-2 py-1"
                  >
                    <Typography variant="small-semibold" className="text-grey">
                      {tripPlace.trip.id} - {TripProperties.getTripLabel(tripPlace.trip)}
                    </Typography>
                    <CenterCapsuleInformation
                      hideTotal
                      attributedPlaces={tripPlace.numberOfStudentsPlacesAffected}
                      blockedPlaces={tripPlace.numberOfBlockedPlaces}
                      total={tripPlace.numberOfStudentsPlacesRequested}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          '-'
        )
      }
    }
  ]

  // wait for the initial center pre-selection and its age check, so the table does not repaint red
  const isInitializing =
    !data ||
    !centers ||
    (!!affectableEnrollments?.length && input.length === 0) ||
    (complianceByEnrollmentId.size === 0 && ageComplianceQueries.some((query) => query.isLoading))

  if (isInitializing) return <Loader center />

  return (
    <div>
      <div className={className}>
        <DataTable<EnrollmentAffectedEnrollmentFormType>
          data={enrollments ?? []}
          columns={columns}
          disableHover
          rowCount={enrollmentIds?.length ?? 0}
          paginated={false}
        />
      </div>
      <ActionButtons
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={() => {
          if (incompatibleAgeRows.length) {
            toggleModal('age-mismatch')
            return
          }
          handleSubmit(input.filter((i) => i.center))
        }}
      />

      {/* age mismatch warning modal */}
      <Modal
        icon={CheckMarkIcon}
        variant={'confirmation'}
        isOpen={modalState['age-mismatch']}
        onCancel={() => closeModel('age-mismatch')}
        title={`${incompatibleAgeRows
          .map(({ name, age }) => (age !== null ? `${name} (${age} ans)` : name))
          .join(
            ', '
          )} : l'âge ne respecte pas l'âge requis pour le centre sélectionné. Voulez-vous confirmer l'affectation quand même ?`}
        actionButtonsOptions={{
          cancelText: 'Fermer',
          confirmText: 'Confirmer quand même',
          onConfirm: () => handleSubmit(input.filter((i) => i.center))
        }}
        isLoading={isLoading}
      />
    </div>
  )
}
